import React, { useMemo, useState } from 'react';
import cn from 'classnames';
import { format } from 'date-fns';
import { useRouter } from 'next/router';
import { IconButton, Spinner } from 'components';
import { Checkbox } from 'components/form';
import { ButtonV3 } from 'components/ComponentV2';
import { Row } from 'components/layout';
import { ActionMenu } from 'components/ActionMenu/ActionMenu.component';
import Modal from 'components/Modal';
import Avatar from 'components/Avatar';

import { useResponsive } from 'hooks/useResponsive';
import { CommunityTypes, CommunityAttributes } from 'lib/models/community';
import { PAGE_ROUTES } from 'lib/page-routes';
import { getRandomInt, randomBGColor } from 'utils/format';
import { MessageModal } from '../CommunityScreeningModal/MessageModal/MessageModal.component';

import styles from './SubscribersTable.module.scss';
import DeleteMember from './DeleteMember';

export const tableColumns = [
  {
    id: 1,
    field: 'email',
    label: 'Email',
    width: '75%',
  },
  {
    id: 2,
    field: 'subscribed',
    label: 'Joined',
    width: '15%',
  },
  {
    id: 3,
    field: '',
    label: '',
    width: '10%',
  },
];

export const attendeesColumns = [
  {
    id: 1,
    field: 'name',
    label: 'Name',
    width: '75%',
  },
  {
    id: 2,
    field: 'event_registered',
    label: 'Event registered',
    width: '15%',
  },
  {
    id: 3,
    field: '',
    label: '',
    width: '10%',
  },
];

interface SubscribersTableProps {
  rows: CommunityAttributes[];
  isLoading: boolean;
  showCheckbox: boolean;
  communityType: string;
  selectedRowsIds?: Array<string>;
  setSelectedRows?: (value: boolean, id: string) => void;
  isAllRowsSelected?: boolean;
  setIsAllRowsSelected?: () => void;
  onOpenScreeningModal?: () => void;
  handleApplicationSelect?: (id: string) => void;
  refetch: () => void;
  reftechDashboard: () => void;
  onApproval: (ids: string[]) => void;
  onRejection: (ids: string[]) => void;
  clearSelectRows: () => void;
  onInviteAttendee: (attendee: {
    first_name: string;
    last_name: string;
    email: string;
  }) => void;
  onSendMessage: (message: string, candidate_ids: string[]) => Promise<void>;
  onDelete: (ids: string[]) => void;
}

export const SubscribersTable: React.FC<SubscribersTableProps> = ({
  rows,
  isLoading,
  showCheckbox,
  communityType,
  selectedRowsIds,
  setSelectedRows,
  isAllRowsSelected,
  setIsAllRowsSelected,
  onOpenScreeningModal,
  handleApplicationSelect,
  refetch,
  reftechDashboard,
  onApproval,
  onRejection,
  clearSelectRows,
  onInviteAttendee,
  onSendMessage,
  onDelete,
}) => {
  const screens = useResponsive();
  const router = useRouter();
  const [isDeleteAccountModalOpen, setIsDeleteAccountModalOpen] =
    useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] =
    React.useState<CommunityAttributes | null>(null);

  const handlePreventDefault = (
    e: React.MouseEvent<HTMLInputElement> | React.ChangeEvent<HTMLInputElement>
  ) => {
    if (router.pathname !== PAGE_ROUTES.DASHBOARD) {
      e?.preventDefault();
      e?.stopPropagation();
    }
  };

  const getActionOptions = (
    selectedApplication: CommunityAttributes,
    invited: boolean,
    candidate_id: string
  ) => {
    return [
      ...(communityType === CommunityTypes.ATTENDEES && !invited
        ? [
            {
              id: '2',
              label: 'Invite',
              onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
                e?.preventDefault();
                e?.stopPropagation();
                setSelectedApplication(selectedApplication);
                onInviteAttendee({
                  first_name: selectedApplication?.first_name || '',
                  last_name: selectedApplication?.last_name || '',
                  email: selectedApplication?.email,
                });
              },
            },
          ]
        : []),
      ...(communityType !== CommunityTypes.INVITED ||
      (communityType === CommunityTypes.INVITED && candidate_id)
        ? [
            {
              id: '1',
              label: 'Message',
              onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
                event.preventDefault();
                event?.stopPropagation();
                setIsMessageModalOpen(true);
                setSelectedApplication(selectedApplication);
              },
            },
          ]
        : []),
      ...(communityType !== CommunityTypes.ATTENDEES
        ? [
            {
              id: '3',
              label: 'Delete',
              onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
                e?.preventDefault();
                e?.stopPropagation();
                setIsDeleteAccountModalOpen(true);
                setSelectedApplication(selectedApplication);
              },
            },
          ]
        : []),
    ];
  };

  const tableData = useMemo(() => {
    const isAttendees = communityType === CommunityTypes.ATTENDEES;
    const isInvited = communityType === CommunityTypes.INVITED;
    const isUnSubscribed = communityType === CommunityTypes.UNSUBSCRIBED;
    const isSubscriptionRequest =
      communityType === CommunityTypes.SUBSCRIPTION_REQUESTS;

    const columns = isAttendees ? attendeesColumns : tableColumns;
    const allowScreeningMode =
      communityType === CommunityTypes.SUBSCRIBED ||
      isAttendees ||
      isSubscriptionRequest;

    return (
      <table>
        {rows.length > 0 && (
          <thead>
            <tr>
              {columns.map((column, index) => (
                <th key={column.id} style={{ width: column.width }}>
                  {index === 0 ? (
                    <Row isFullWidthRow align="center" columnGap={8}>
                      {showCheckbox ? (
                        <Checkbox
                          checked={isAllRowsSelected}
                          onClick={handlePreventDefault}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            handlePreventDefault(e);
                            setIsAllRowsSelected && setIsAllRowsSelected();
                          }}
                          className={styles.rowCheckbox}
                        />
                      ) : null}
                      <span>{column.label}</span>
                    </Row>
                  ) : (
                    <span>
                      {isInvited && column.field === CommunityTypes.SUBSCRIBED
                        ? 'Date'
                        : isUnSubscribed &&
                          column.field === CommunityTypes.SUBSCRIBED
                        ? 'Left'
                        : column.label}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows?.map((row: CommunityAttributes) => {
            const {
              id,
              email,
              created_at,
              event_registrations_count,
              avatar_url,
              name,
              invited,
              updated_at,
              candidate_id,
            } = row || {};
            const isChecked = showCheckbox && selectedRowsIds?.includes(id);
            const randomNumber = getRandomInt(0, 10, true);
            const color = randomBGColor[randomNumber as number];

            return (
              <tr
                key={id}
                className={cn({ [styles.selectedRow]: isChecked })}
                onClick={() => {
                  if (allowScreeningMode) {
                    onOpenScreeningModal && onOpenScreeningModal();
                    handleApplicationSelect && handleApplicationSelect(id);
                  }
                }}
              >
                <td>
                  <div className={styles.nameSection}>
                    {showCheckbox ? (
                      <Checkbox
                        checked={isChecked}
                        onClick={handlePreventDefault}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          handlePreventDefault(e);
                          setSelectedRows &&
                            setSelectedRows(e?.target?.checked, id);
                        }}
                        className={styles.rowCheckbox}
                      />
                    ) : null}
                    {isAttendees ? (
                      <>
                        <Avatar
                          size="small"
                          icon={avatar_url || ''}
                          name={name || email}
                          className={styles.avatarImage}
                          style={{ backgroundColor: color }}
                        />
                        <p>{name ? name : email ? email.split('@')[0] : ''}</p>
                        {invited ? (
                          <span className={cn(styles.status)}>Invited</span>
                        ) : null}
                      </>
                    ) : (
                      <p>{email ? email : name ? name : ''}</p>
                    )}
                  </div>
                </td>
                <td className={styles.text}>
                  {isAttendees ? (
                    <span>{event_registrations_count || 0}</span>
                  ) : isUnSubscribed ? (
                    <span>
                      {updated_at
                        ? format(new Date(updated_at), 'MMM d, yyyy')
                        : ''}
                    </span>
                  ) : (
                    <span>
                      {created_at
                        ? format(new Date(created_at), 'MMM d, yyyy')
                        : ''}
                    </span>
                  )}
                </td>
                <td>
                  <div className={styles.actionsSection}>
                    {communityType === CommunityTypes.SUBSCRIPTION_REQUESTS ? (
                      <div className={styles.buttonGroup}>
                        <ButtonV3
                          className={styles.subscribersListBtn}
                          color="secondary"
                          size="small"
                          startIcon="valid-check-mark"
                          onClick={(e) => {
                            e?.preventDefault();
                            e?.stopPropagation();
                            onAcceptAction([id]);
                          }}
                        >
                          Accept
                        </ButtonV3>
                        <ButtonV3
                          className={styles.subscribersListBtn}
                          color="secondary"
                          size="small"
                          startIcon="close"
                          onClick={(e) => {
                            e?.preventDefault();
                            e?.stopPropagation();
                            onDeclineAction([id]);
                          }}
                        >
                          Decline
                        </ButtonV3>
                      </div>
                    ) : (
                      <ActionMenu
                        className={styles.buttonsMenu}
                        listClassName={styles.actionMeu}
                        triggerIconClassName={styles.triggerIcon}
                        options={getActionOptions(row, invited, candidate_id)}
                        subOptionsAlignClassName={{
                          right: styles.alignClassNameRight,
                          left: styles.alignClassNameLeft,
                        }}
                      />
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }, [
    rows,
    isLoading,
    showCheckbox,
    communityType,
    isLoading,
    selectedRowsIds,
    isAllRowsSelected,
  ]);

  if (isLoading) {
    return (
      <section className={styles.EventTableEmptyContainer}>
        <Spinner size="small" />
      </section>
    );
  }

  const toggleMessageModal = () => {
    setIsMessageModalOpen(!isMessageModalOpen);
  };

  const onAcceptAction = async (ids: string[]) => {
    await onApproval(ids);
    refetch();
    reftechDashboard();
  };

  const onDeclineAction = async (ids: string[]) => {
    await onRejection(ids);
    refetch();
    reftechDashboard();
  };

  const onMessageAction = async (message: string) => {
    if (selectedApplication?.id) {
      await onSendMessage(message, [selectedApplication?.id]);
      toggleMessageModal();
    }
  };

  const onDeleteAction = async () => {
    if (selectedApplication?.id) {
      setIsDeleteLoading(true);
      await onDelete([selectedApplication.id]);
      refetch();
      reftechDashboard();
      setIsDeleteAccountModalOpen(false);
      setIsDeleteLoading(false);
    }
  };
  return (
    <>
      <section className={styles.EventTable} data-testid="EventTable">
        {tableData}
        {selectedRowsIds?.length ? (
          <div className={styles.selectionBox}>
            <div className={styles.selectionBoxContainer}>
              <div className={styles.selectionText}>
                {selectedRowsIds?.length} selected
              </div>
              <div className={styles.btnGroup}>
                <ButtonV3
                  className={styles.subscribersListBtn}
                  color="secondary"
                  size="small"
                  startIcon="valid-check-mark"
                  onClick={(e) => {
                    e?.preventDefault();
                    e?.stopPropagation();
                    onAcceptAction(selectedRowsIds);
                    clearSelectRows();
                  }}
                >
                  Accept
                </ButtonV3>
                <ButtonV3
                  className={styles.subscribersListBtn}
                  color="secondary"
                  size="small"
                  startIcon="close"
                  onClick={(e) => {
                    e?.preventDefault();
                    e?.stopPropagation();
                    onDeclineAction(selectedRowsIds);
                    clearSelectRows();
                  }}
                >
                  Decline
                </ButtonV3>
              </div>
            </div>
          </div>
        ) : (
          <></>
        )}
      </section>
      {isMessageModalOpen ? (
        <MessageModal
          isOpen={isMessageModalOpen}
          onClose={toggleMessageModal}
          isBulkMode={false}
          candidateName={
            selectedApplication
              ? (selectedApplication?.name ||
                  selectedApplication?.first_name ||
                  selectedApplication?.last_name ||
                  selectedApplication?.email?.split('@')?.[0]) ??
                ''
              : ''
          }
          onSendMessage={onMessageAction}
        />
      ) : null}
      {isDeleteAccountModalOpen ? (
        <Modal
          isOpen={isDeleteAccountModalOpen}
          onRequestClose={() => setIsDeleteAccountModalOpen(false)}
          isFullScreen={false}
          className={styles.modal}
          contentClassName={cn(
            styles.modalContainerContent,
            styles.modalContainerItemInfo
          )}
        >
          {screens.sm ? (
            <IconButton
              iconName="chevron-left"
              size="xxlarge"
              className={styles.modalChevron}
              onClick={() => setIsDeleteAccountModalOpen(false)}
            />
          ) : null}
          {!screens.sm ? (
            <IconButton
              iconName="close"
              size="small"
              className={styles.modalClose}
              onClick={() => setIsDeleteAccountModalOpen(false)}
            />
          ) : null}
          <DeleteMember
            selectedEmail={selectedApplication?.email}
            onCancel={() => setIsDeleteAccountModalOpen(false)}
            onSubmit={onDeleteAction}
            className={styles.modalContainerContentDelete}
            isButtonLoading={isDeleteLoading}
          />
        </Modal>
      ) : null}
    </>
  );
};

SubscribersTable.displayName = 'SubscribersTable';
